import type { ShopperProducts } from '@/scapi';
import { use, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import ProductTabsDetails from './product-tabs-details';
import ProductTabsLabel from './product-tabs-label';
import ProductTabsMark from './product-tabs-mark';
import ProductTabsSupplyChains from './product-tabs-supply-chains';
import type { BrandContent } from '@/lib/api/fetch-brand';
import type { SupplyChainContent } from '@/lib/api/fetch-supply-chain';
import BundleContents from './bundle-contents';

export type ProductTabId = 'details' | 'bundleContents' | 'label' | 'brand' | 'supplyChain';

export default function ProductTabs({
    product,
    labelImageProduct,
    brandCustomObject,
    supplyChainCustomObject,
}: {
    product: ShopperProducts.schemas['Product'];
    /** SKU whose `viewType: label` images are shown; falls back to `product` when missing. */
    labelImageProduct?: ShopperProducts.schemas['Product'];
    brandCustomObject: Promise<BrandContent | undefined>;
    supplyChainCustomObject: Promise<SupplyChainContent | undefined>;
}) {
    const { t } = useTranslation('product');
    const brand = use(brandCustomObject);
    const supplyChain = use(supplyChainCustomObject);
    const imageProduct = labelImageProduct ?? product;

    const sections = useMemo(() => {
        type Section = {
            id: ProductTabId;
            label: string;
            content: ReactNode;
        };
        const base: Section[] = [
            { id: 'details', label: t('tabs.details.title'), content: <ProductTabsDetails product={product} /> },
        ];
        const bundleContents =
            product.type?.bundle && (product.bundledProducts?.length ?? 0) > 1 ? product.bundledProducts : undefined;

        if (bundleContents) {
            base.push({
                id: 'bundleContents',
                label: t('tabs.bundleContents'),
                content: <BundleContents bundledProducts={bundleContents} />,
            });
            return base;
        }

        base.push({
            id: 'label',
            label: t('tabs.label'),
            content: <ProductTabsLabel product={product} labelImageProduct={imageProduct} />,
        });
        if (brand && brand.c_pimBrandName && brand.c_pimBrandDescription) {
            base.push({
                id: 'brand',
                label: t('tabs.brand'),
                content: <ProductTabsMark brand={brand} />,
            });
        }
        if (supplyChain && supplyChain.c_pimSupplyChainName && supplyChain.c_pimSupplyChainDescription) {
            base.push({
                id: 'supplyChain',
                label: t('tabs.supplyChain'),
                content: <ProductTabsSupplyChains supplyChain={supplyChain} />,
            });
        }
        return base;
    }, [product, imageProduct, brand, supplyChain, t]);

    const defaultTab = sections[0]?.id ?? 'details';

    return (
        <Tabs defaultValue={defaultTab} className="gap-8 max-w-screen-2xl mx-auto w-full min-w-0">
            <TabsList
                scrollActiveTabOnMobile
                className="flex w-full max-w-full min-w-0 bg-transparent gap-3 overflow-x-auto scrollbar-hide overflow-y-hidden justify-start">
                {sections.map((section) => (
                    <TabsTrigger
                        key={section.id}
                        value={section.id}
                        data-analytics-event="tab_click"
                        data-analytics-payload={JSON.stringify({ click_text: section.label })}
                        className="label-m shrink-0 bg-sand-95 text-neutral-10 rounded-full hover:bg-neutral-10 hover:text-neutral-white data-[state=active]:bg-neutral-10 data-[state=active]:text-neutral-white w-fit md:flex-none md:w-fit">
                        {section.label}
                    </TabsTrigger>
                ))}
            </TabsList>
            {sections.map((section) => (
                <TabsContent key={section.id} value={section.id} className="w-full">
                    {section.content}
                </TabsContent>
            ))}
        </Tabs>
    );
}
